import { getAPIErrorDetailOrMessage } from "@/api/helpers";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { translation } from "@/i18n";
import { nsDialog } from "@/i18n/locales/dialog";
import { useWarningButtonStyles } from "@/styles";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  ProgressBar,
} from "@fluentui/react-components";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { OperationStatus, OperationStatusLabel } from "../DialogStatus";
import styles from "./index.module.css";

const useTranslation = translation.use(nsDialog);

export type BulkDialogPhaseType =
  | "confirm"
  | "in_progress"
  | "success"
  | "failed";
type State = { status: OperationStatus; message?: string };
type RawState<T> = T & State;
export interface BulkDialogProps<
  S extends string,
  T extends { [key: string]: any } & { [K in S]: string },
> {
  title: string;
  displayMessage?: string | ReactNode;
  startButtonText: string;
  isThemeRed?: boolean;
  open: boolean;
  rows: T[];
  rowKey: S;
  operationName: string;
  run: (row: T) => Promise<State | void>;
  renderItem: (row: T, phase: BulkDialogPhaseType) => ReactNode;
  onClose: (props: {
    phase: BulkDialogPhaseType;
    states: RawState<T>[];
  }) => void;
  /** 開始ボタンを無効化する（バリデーション連動等） */
  isStartDisabled?: boolean;
}

export const useBulkDialog = <
  S extends string,
  T extends { [key: string]: any } & { [K in S]: string },
>({
  title,
  displayMessage,
  startButtonText,
  isThemeRed,
  open,
  rows,
  rowKey,
  operationName,
  run,
  renderItem,
  onClose,
  isStartDisabled = false,
}: BulkDialogProps<S, T>) => {
  const { t } = useTranslation();
  const { showErrorNotification, showSuccessNotification } =
    useAppNotificationManager();
  const [phase, setPhase] = useState<BulkDialogPhaseType>("confirm");
  const [stopOnFailure, setStopOnFailure] = useState(true);
  // NOTE: 即時反映するものとしてrefで管理
  const abortRequested = useRef<boolean>(false);

  const [rowStates, setRawStates] = useState<RawState<T>[]>([]);
  const upsertRawState = useCallback(
    (newRawState: RawState<T>) => {
      setRawStates((prev) =>
        prev.map((r) => {
          if (r[rowKey]! === newRawState[rowKey]!) {
            return newRawState;
          }
          return r;
        })
      );
    },
    [rowKey]
  );

  useEffect(() => {
    if (open) {
      setPhase("confirm");
      abortRequested.current = false;
      // 初期 waiting 付与
      setRawStates(
        rows.map((row) => {
          return { ...row, status: "waiting" as OperationStatus };
        })
      );
    }
  }, [open]);

  const runBulk = useCallback(async () => {
    setPhase("in_progress");
    let successCount = 0;
    let failedCount = 0;
    for (const row of rows) {
      if (abortRequested.current) {
        break;
      }
      upsertRawState({ ...row, status: "in_progress" });
      try {
        const result = (await run(row)) || {
          status: "success" as OperationStatus,
        };
        result.status === "success" ? successCount++ : failedCount++;
        upsertRawState({
          ...row,
          status: result.status,
          message: result?.message ?? undefined,
        });
      } catch (e: any) {
        const { errorMessage } = getAPIErrorDetailOrMessage(e);
        failedCount++;
        upsertRawState({
          ...row,
          status: "failed",
          message: errorMessage,
        });
        if (stopOnFailure) {
          abortRequested.current = true;
          break;
        }
      }
    }
    if (abortRequested.current && successCount + failedCount < rows.length) {
      setPhase("failed");
      showErrorNotification(
        t.dialog.bulk.stoppedNotification({ operationName }),
        undefined
      );
    } else if (failedCount) {
      setPhase("failed");
      showErrorNotification(
        t.dialog.bulk.partialFailureNotification({ operationName }),
        undefined
      );
    } else {
      setPhase("success");
      showSuccessNotification(
        t.dialog.bulk.completedNotification({ operationName })
      );
    }
  }, [
    rows,
    run,
    upsertRawState,
    stopOnFailure,
    showErrorNotification,
    showSuccessNotification,
    operationName,
    t,
  ]);

  const rowStatesElement = useMemo(() => {
    if (rowStates.length === 0)
      return <div>{t.dialog.bulk.noTargetSelected({ operationName })}</div>;
    return (
      <div className={styles.rows}>
        {rowStates.map((i) => (
          <div key={i[rowKey]} className={styles.row}>
            <div className={styles.item}>{renderItem(i, phase)}</div>
            <span className={styles.status}>
              <OperationStatusLabel status={i.status} message={i.message} />
            </span>
          </div>
        ))}
      </div>
    );
  }, [rowStates, phase, renderItem, operationName, t]);

  const buttonClasses = useWarningButtonStyles();
  const renderBulkDialogs = useCallback(() => {
    const canStart =
      rowStates.length > 0 && phase === "confirm" && !isStartDisabled;

    const totalCount = rowStates.length;
    const successCount = rowStates.filter((i) => i.status === "success").length;
    const failedCount = rowStates.filter((i) => i.status === "failed").length;
    const processedCount = successCount + failedCount;
    const progress = totalCount === 0 ? 0 : processedCount / totalCount;

    return (
      <Dialog
        open={open}
        onOpenChange={(_, data) => {
          if (!data.open) {
            onClose({ phase, states: rowStates });
          }
        }}
        modalType={phase === "in_progress" ? "alert" : "modal"}
      >
        <DialogSurface style={{ width: 640, maxWidth: "90vw" }}>
          <DialogBody>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
              {displayMessage ?? null}
              <div style={{ marginTop: 12, marginBottom: 8, height: "1.2em" }}>
                {t.dialog.bulk.targetCount({ count: rowStates.length })}
              </div>
              <div
                style={{ margin: "4px 0 4px", fontSize: 12, height: "1.2em" }}
              >
                {phase === "in_progress" && (
                  <ProgressBar value={progress} max={1} />
                )}
                {phase === "success" && (
                  <span>
                    {t.dialog.bulk.resultSummary({
                      success: successCount,
                      failed: failedCount,
                    })}{" "}
                    {abortRequested.current
                      ? t.dialog.bulk.abortedSuffix()
                      : null}
                  </span>
                )}
              </div>
              {rowStatesElement}
            </DialogContent>
            <DialogActions fluid style={{ height: "2em" }}>
              <Checkbox
                checked={stopOnFailure}
                disabled={phase !== "confirm"}
                onChange={(_, d) => setStopOnFailure(!!d.checked)}
                label={t.dialog.bulk.stopOnFailureLabel()}
              />
              {phase !== "in_progress" && (
                <Button
                  appearance={isThemeRed ? undefined : "primary"}
                  onClick={() => {
                    abortRequested.current = false;
                    runBulk();
                  }}
                  disabled={!canStart}
                  className={
                    canStart && isThemeRed ? buttonClasses.warning : ""
                  }
                  style={{ minWidth: "7em", padding: "0 1em" }}
                >
                  {startButtonText}
                </Button>
              )}
              {phase === "in_progress" && (
                <Button
                  disabled={abortRequested.current}
                  onClick={() => {
                    abortRequested.current = true;
                  }}
                  style={{ minWidth: "7em", padding: "0 1em" }}
                >
                  {abortRequested.current
                    ? t.dialog.bulk.stoppingButton()
                    : t.dialog.bulk.stopButton()}
                </Button>
              )}
              <Button
                disabled={phase === "in_progress"}
                onClick={() => {
                  onClose({ phase, states: rowStates });
                }}
                style={{ minWidth: "7em", padding: "0 1em" }}
              >
                {phase === "success"
                  ? t.dialog.common.completeButton()
                  : phase === "failed"
                    ? t.dialog.common.backButton()
                    : t.dialog.common.cancelButton()}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    );
  }, [
    open,
    title,
    displayMessage,
    rowStates,
    phase,
    abortRequested,
    rowStatesElement,
    stopOnFailure,
    isThemeRed,
    isStartDisabled,
    startButtonText,
    onClose,
    t,
  ]);
  return {
    renderBulkDialogs,
  };
};
